/**
 * Card-number redaction (PCI) for text fields built on `UIInputTextBase`.
 *
 * `UIInputText` and `UIInputTextArea` redact through their `isSanitised` prop. The
 * Base component has no such prop, so a field that has to stay on Base — it is not a
 * vee-validate field, or must not register in the form around it — runs the value from
 * its `update:modelValue` through {@link redactCardNumbers} and keeps the result.
 *
 * Detection is ui-lib's `sanitiseText`, so both routes agree on what a card number is.
 * This is separate from `chdDetection`, which backs the chat composer's notice-and-mask
 * flow and is deliberately stricter about what it calls card data.
 */
import { sanitiseText } from '@foundever/ui-lib'

import { trackFrontendRedaction } from '@/utils/chdRedactionTracking'

export interface IRedactCardNumbersOptions {
  /**
   * The field's native control (`UIInputTextBase` exposes it as `inputElement`).
   * Pass it so the field displays the redacted text too — see {@link redactCardNumbers}.
   */
  inputElement?: HTMLInputElement | HTMLTextAreaElement | null
  /**
   * Drop the card number instead of putting the redaction text in its place. For a
   * field too short to hold that text, or one whose value is not prose.
   */
  shouldRemove?: boolean
  /** Truncate the result to the field's length cap; the redaction text can outgrow it. */
  maxLength?: number
  /** Names the field in the `chd_redacted_frontend` analytics event. */
  source?: string
  /** Off for a repeat pass over text whose redaction the first pass already reported. */
  isTracked?: boolean
}

/**
 * Returns `value` with every card number replaced by ui-lib's redaction text, or
 * removed when `shouldRemove` is set. A value without a card number comes back as is.
 */
export const redactCardNumbers = (
  value: string,
  {
    inputElement,
    shouldRemove = false,
    maxLength,
    source = 'text_field',
    isTracked = true,
  }: IRedactCardNumbersOptions = {},
): string => {
  const { sanitisedText, sanitisationErrors: cardNumberMatches } =
    sanitiseText(value)
  if (!cardNumberMatches.length) return value

  if (isTracked) trackFrontendRedaction(source, cardNumberMatches.length)

  const withoutCardNumbers = shouldRemove
    ? // Last match first, so removing one does not shift the offsets of the rest.
      [...cardNumberMatches]
        .sort((first, second) => second.index - first.index)
        .reduce(
          (text, { index, match }) =>
            text.slice(0, index) + text.slice(index + match.length),
          value,
        )
    : sanitisedText
  const redacted =
    maxLength === undefined
      ? withoutCardNumbers
      : withoutCardNumbers.slice(0, maxLength)

  // A Base field is controlled: it only redraws when the value bound to it changes. If
  // the redacted text equals what the parent already holds (a number pasted over it),
  // nothing re-renders and the control would go on showing the raw number — so write
  // it to the control directly, as `UIInputText` does.
  if (inputElement) inputElement.value = redacted

  return redacted
}
